import { ref } from "vue";
import {
  getAuth,
  OAuthProvider,
  reauthenticateWithPopup,
} from "firebase/auth";
import { cloudFunctions } from "@/store/cloudFunctions.js";
import { authUtils } from "@/scripts/authUtils.js";
import { useToast } from "@/composables/useToast.js";
import {
  markAccountDeletionDispatched,
  clearAccountDeletion,
} from "@/composables/accountDeletionState.js";
import { useI18n } from "vue-i18n";

// The backend's deleteAccount handler can take up to its 300s ceiling (heavy S3
// deletion). The client abort must be the backend ceiling PLUS grace for the
// response to travel back, or a deletion that succeeds near the limit would be
// aborted and misreported as a network failure while the server actually
// completed. 30s grace beyond the 300s backend ceiling; the timer also starts
// only after the ID token is retrieved (below), not before.
const DELETE_TIMEOUT_MS = 330000;

function isCapacitor() {
  return (
    typeof window !== "undefined" &&
    window.Capacitor?.isNativePlatform?.() === true
  );
}

/**
 * In-app account deletion flow (App Store Guideline 5.1.1(v)). Handles the Apple
 * re-auth needed to revoke Sign in with Apple tokens, POSTs deleteAccount, and
 * signs out on success. Returns reactive `isDeleting` and a `deleteAccount()`
 * action; the caller owns the confirmation UI.
 */
export function useAccountDeletion() {
  const isDeleting = ref(false);
  const { showToast } = useToast();
  const { t } = useI18n();

  /**
   * Fresh Apple token material for revocation, or null for non-Apple accounts
   * (nothing to revoke for Google). Covers linked-provider users: if the account
   * links Apple at all, the Apple path applies even when the current session
   * came in via Google. Throws if the user cancels/fails re-auth.
   */
  async function getAppleTokenIfNeeded() {
    const user = getAuth().currentUser;
    const providerIds = (user?.providerData || []).map((p) => p.providerId);
    if (!providerIds.includes("apple.com")) {
      return null;
    }
    if (isCapacitor()) {
      // skipNativeAuth: auth state lives only in the web SDK, so we take the raw
      // authorization code and let the backend spend it (the native currentUser
      // needed for on-device revocation doesn't exist here).
      const plugin = window.Capacitor.Plugins.FirebaseAuthentication;
      const result = await plugin.signInWithApple({ skipNativeAuth: true });
      const token = result?.credential?.authorizationCode;
      if (!token) throw new Error("Apple re-auth returned no authorization code");
      // platform tells the backend which Apple client the token was issued to:
      // native iOS codes belong to the app's bundle ID, while Android (and web)
      // go through the web Services ID flow. The backend picks the matching
      // identity for the revocation exchange.
      return { tokenType: "CODE", token, platform: window.Capacitor.getPlatform() };
    }
    const provider = new OAuthProvider("apple.com");
    const result = await reauthenticateWithPopup(user, provider);
    const token = OAuthProvider.credentialFromResult(result)?.accessToken;
    if (!token) throw new Error("Apple re-auth returned no access token");
    return { tokenType: "ACCESS_TOKEN", token, platform: "web" };
  }

  /**
   * Run the deletion. Double-submit guarded. On success signs out (which clears
   * FCM tokens, native + web auth, and redirects home) and toasts. On any
   * failure nothing is signed out — the request is retryable end-to-end.
   * @returns {Promise<{ok: boolean, reason?: string}>}
   */
  async function deleteAccount() {
    if (isDeleting.value) return { ok: false, reason: "in-progress" };
    isDeleting.value = true;

    // Fail closed before anything user-visible (Apple re-auth) or destructive
    // happens if the endpoint isn't configured for this environment. An unfilled
    // placeholder (or any non-absolute URL) is resolved against the app origin
    // and answered by the SPA/Capacitor catch-all with a 200 + index.html, which
    // would be misread as a successful deletion and sign the user out of a
    // still-live account. Never send the request in that case.
    const endpoint = cloudFunctions.getDeleteAccountApi();
    if (!/^https?:\/\//i.test(endpoint)) {
      console.error("Account deletion endpoint is not configured:", endpoint);
      showToast(t("account.dangerZone.error"), "error");
      isDeleting.value = false;
      return { ok: false, reason: "unconfigured" };
    }

    let appleToken;
    try {
      appleToken = await getAppleTokenIfNeeded();
    } catch (reauthError) {
      // Cancelled or failed re-auth: abort before anything is sent. Nothing is
      // deleted; the user stays signed in.
      console.warn("Account deletion re-auth aborted:", reauthError);
      showToast(t("account.dangerZone.reauthCancelled"), "info");
      isDeleting.value = false;
      return { ok: false, reason: "reauth" };
    }

    let token;
    // Captured before the request, because after a successful deletion there
    // is no currentUser left to ask. The flag records which account the
    // deletion was for so a later sign-in by someone else can clear it.
    const deletingUid = getAuth().currentUser?.uid || null;
    try {
      token = await getAuth().currentUser.getIdToken();
    } catch (error) {
      console.error("Account deletion: failed to get ID token:", error);
      showToast(t("account.dangerZone.error"), "error");
      isDeleting.value = false;
      return { ok: false, reason: "network" };
    }

    // Start the abort timer only after the token is in hand, so token refresh
    // latency doesn't eat into the request's grace window.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DELETE_TIMEOUT_MS);
    // From here on the server may already be tearing users/{uid} down, and the
    // dashboard is still listening to it. Tell the app so the null snapshots
    // that produces are not mistaken for a brand new account (which would mount
    // the onboarding wizard behind the confirmation sheet, and re-create the
    // node via the region/timezone startup writes). Every failure path below
    // clears it again — those users keep a live account.
    //
    // Not set at all if auth changed under us during the token fetch (another
    // tab signing someone else in): the account being deleted is no longer the
    // one this context is showing, so protecting it here would suppress the
    // NEW user's dashboard instead. The deletion itself still goes ahead — it
    // carries the token that was already issued for `deletingUid`.
    const uidNow = getAuth().currentUser?.uid || null;
    let protectionToken = null;
    if (!uidNow || uidNow === deletingUid) {
      protectionToken = markAccountDeletionDispatched(deletingUid);
    } else {
      console.warn(
        "Account deletion: the signed-in account changed mid-request; not suppressing dashboard startup for it"
      );
    }
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ appleToken: appleToken || null }),
        signal: controller.signal,
      });
      if (!response.ok) {
        clearAccountDeletion(protectionToken);
        showToast(t("account.dangerZone.error"), "error");
        isDeleting.value = false;
        return { ok: false, reason: "server" };
      }
      // A 2xx alone is not proof of deletion: a misrouted request answered by an
      // SPA/proxy catch-all also returns 200 (with an HTML body, not our JSON).
      // Require the backend's explicit `{ status: "deleted" }` confirmation
      // before signing out, so a false success can never delete-then-not-delete.
      let payload = null;
      try {
        payload = await response.json();
      } catch (parseError) {
        payload = null;
      }
      if (payload?.status !== "deleted") {
        console.error("Account deletion: unexpected non-deletion response", payload);
        clearAccountDeletion(protectionToken);
        showToast(t("account.dangerZone.error"), "error");
        isDeleting.value = false;
        return { ok: false, reason: "server" };
      }
    } catch (error) {
      console.error("Account deletion request failed:", error);
      clearAccountDeletion(protectionToken);
      showToast(t("account.dangerZone.error"), "error");
      isDeleting.value = false;
      return { ok: false, reason: "network" };
    } finally {
      clearTimeout(timeoutId);
    }

    // Success: sign out (handles FCM cleanup + native/web sign-out + redirect)
    // and toast. Leave isDeleting true — the view is unmounting.
    await authUtils.signOut();
    showToast(t("account.dangerZone.deleted"), "success");
    return { ok: true };
  }

  return { isDeleting, deleteAccount };
}
